import React, { useState } from "react";
import {
  StyleSheet,
  Text,
  TextInput,
  type TextStyle,
  View,
  type ViewStyle,
} from "react-native";
import { Ionicons } from "@expo/vector-icons";

import {
  getEmailTypoSuggestion,
  isValidEmailAddress,
} from "@/schemas/forms/email";

type EmailInputProps = {
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  containerStyle?: ViewStyle;
  inputStyle?: TextStyle;
  iconColor?: string;
};

const EmailInput: React.FC<EmailInputProps> = ({
  value,
  onChangeText,
  placeholder,
  containerStyle,
  inputStyle,
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const [isValid, setIsValid] = useState(true);
  const [validationMessage, setValidationMessage] = useState("");
  const [typoSuggestion, setTypoSuggestion] = useState("");

  const validateEmail = (email: string): boolean => {
    const emailValidation = isValidEmailAddress(email);
    setValidationMessage(emailValidation.message);

    // Check for typo suggestions on valid emails
    if (emailValidation.isValid) {
      const suggestion = getEmailTypoSuggestion(email);
      setTypoSuggestion(suggestion ? `Did you mean ${suggestion}?` : "");
    } else {
      setTypoSuggestion("");
    }

    return emailValidation.isValid;
  };

  const handleChangeText = (text: string): void => {
    onChangeText(text);
    const valid = text === "" || validateEmail(text);
    setIsValid(valid);
    if (text === "") {
      setValidationMessage("");
      setTypoSuggestion("");
    }
  };

  const handleBlur = (): void => {
    setIsFocused(false);
    const valid = value === "" || validateEmail(value);
    setIsValid(valid);
  };

  return (
    <View>
      <View
        style={[
          styles.container,
          containerStyle,
          isFocused && styles.focusedContainer,
          !isValid && styles.invalidContainer,
        ]}
      >
        <TextInput
          testID="email-input"
          style={[styles.input, inputStyle]}
          value={value}
          onChangeText={handleChangeText}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
          placeholder={placeholder}
          keyboardType="email-address"
          autoCapitalize="none"
          autoCorrect={false}
          textContentType="emailAddress"
        />
        {isValid && value !== "" && (
          <Ionicons
            name="checkmark-circle"
            size={24}
            color="#48BB78"
            style={styles.validIcon}
          />
        )}
      </View>
      {!isValid && validationMessage && (
        <Text style={styles.errorMessage}>{validationMessage}</Text>
      )}
      {isValid && typoSuggestion && (
        <Text style={styles.suggestionMessage}>{typoSuggestion}</Text>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    borderWidth: 1,
    borderColor: "#E2E8F0",
    borderRadius: 8,
    backgroundColor: "white",
    paddingHorizontal: 12,
    marginBottom: 4,
  },
  focusedContainer: {
    borderColor: "#4299E1",
  },
  invalidContainer: {
    borderColor: "#F56565",
  },
  input: {
    flex: 1,
    fontSize: 16,
    paddingVertical: 12,
    color: "#2D3748",
  },
  validIcon: {
    marginLeft: 8,
  },
  errorMessage: {
    color: "#F56565",
    fontSize: 14,
    marginTop: 4,
    marginHorizontal: 2,
  },
  suggestionMessage: {
    color: "#4A90E2",
    fontSize: 14,
    margin: 2,
    marginHorizontal: 2,
  },
});

export default EmailInput;
